import { Suspense, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useParams,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";

// ==================== LAZY IMPORTS (Code Splitting) ====================
import {
  // Superadmin
  SuperAdminDashboard,
  SuperAdminLayout,
  SuperAdminTenantsManagement,
  SuperAdminUsersManagement,
  SuperAdminCompanyUsersManagement,
  SuperAdminCompaniesManagement,
  SuperAdminSubscriptionsManagement,
  SuperAdminPlansManagement,
  SuperAdminTemplatesManagement,
  SuperAdminPaymentsManagement,
  SuperAdminSecurityManagement,
  SuperAdminEmailManagement,
  SuperAdminAnalytics,
  SuperAdminSessionMetrics,
  SuperAdminSystemLogs,
  SuperAdminAuditLogs,
  SuperAdminPlatformConfig,
  SuperAdminOCRSettings,
  SuperAdminTicketsManagement,
  MonitoringDashboard,
  FeatureFlagsManagement,
  BroadcastManagement,
  QuotasManagement,
  OpenBankingSettings,
  LandingCmsEditor,
  // Accounting
  AccountingDashboard,
  ChartOfAccountsManagement,
  JournalEntriesManagement,
  GeneralBalanceView,
  GeneralLedgerView,
  AccountingExportsPage,
  AccountReconciliationView,
  FinancialStatementsView,
  FiscalYearClosureView,
  MarginDashboard,
  AccountingEntryAndReconciliation,
  AccountingBalanceAndLedger,
  AccountingDeclarationsHub,
  // ERP BTP
  CockpitDashboard,
  TerrainDashboard,
  CollaborationPage,
  ClientPortalView,
  SubcontractorPortalView,
  CollaboratorManagement,
  ProjectsManagement,
  ProjectDetailPage,
  PortfolioDashboardPage,
  ERPSituationsPage,
  ERPDocumentsPage,
  ERPMaterialsPage,
  ERPSubcontractingPage,
  ERPPlanningPage,
  ERPTeamsPage,
  MyDayView,
  FacturerView,
  // ERP Chantier
  ERPLayout,
  TerrainLayout,
  ArgentLayout,
  DossiersLayout,
  AccueilFeed,
  TerrainHome,
  QuickPointing,
  AdvancementCapture,
  IncidentReport,
  ArgentHome,
  FacturationAssistant,
  AlerteDepassement,
  EncaissementsView,
  DossiersHome,
  MaSemaine,
  DetailJournee,
  SoumissionSemaine,
  EquipeTempsReel,
  FeuillesEnAttente,
  // Treasury
  TreasuryDashboard,
  RecurringExpensesManagement,
  BudgetsManagement,
  AlertsManagement,
  RecommendationsManagement,
  HealthScoreManagement,
  ForecastsManagement,
  // CRM
  CRMDashboard,
  LeadsManagement,
  OpportunitiesPipeline,
  SalesTasksManagement,
  AutomationRulesManagement,
  // Interventions
  InterventionsDashboard,
  WorkOrdersManagement,
  PlanningCalendar,
  TimesheetsManagement,
  FieldModeView,
  // Expenses
  OCRInvoiceExtraction,
  ThreeWayMatching,
  // Compliance
  ChorusProManagement,
  ProbatoryArchiveDashboard,
  FacturXDebugPanel,
  // Tax
  FiscalYearManagement,
  TaxRatesManagement,
  // AI Actions
  AIActionsPage,
  // Benchmarks
  BenchmarksDashboard,
  // Financing
  FinancingDashboard,
  // Bank
  BankReconciliation,
  // Subcontracts
  SubcontractsPage,
  // Achats (Unified Module - replaces purchases & expenses)
  AchatsDashboard,
  // Cabinet
  CabinetClientsManagement,
  ClientDossierPage,
  CabinetExportsPage,
} from "./routes/lazyImports";

// ==================== STATIC IMPORTS (Core/Essential) ====================
import AccountingConnectorsSettings from "./features/accounting-connectors/components/AccountingConnectorsSettings";
import ReminderPoliciesManagement from "./features/payments/components/ReminderPoliciesManagement";
import ApiKeysManagement from "./features/admin/components/ApiKeysManagement";
import { EmailLogsManagement } from "./features/admin/components/EmailLogsManagement";
import SignaturesManagement from "./features/signatures/components/SignaturesManagement";
import { AuditLogsManagement } from "./features/admin/components/AuditLogsManagement";
import { WebhooksManagement } from "./features/admin/components/WebhooksManagement";
import BillingDashboard from "./features/billing/components/BillingDashboard";
import { SupportPage, TicketDetail, TicketCreateForm } from "./features/support";
import { HelpCenterPage } from "./features/knowledge-base";
import { SystemAlertsPage } from "./features/system-alerts";
import {
  ClientPortalLoginPage,
  ClientPortalLayout,
  ClientPortalDashboard,
  ClientProjectsList,
  ClientProjectDetail,
  ClientMessagesPanel,
} from "./features/client-portal";
import { DashboardOverview } from "./components/DashboardOverview";
import CheckoutPage from "./features/subscriptions/components/CheckoutPage";
import { ClientsManagementResponsive } from "./features/catalog/components/ClientsManagementResponsive";
import ConfirmDialog from "./features/shared/components/ConfirmDialog";
import PromptDialog from "./features/shared/components/PromptDialog";
import { DashboardMainResponsive } from "./features/shared/components/DashboardMainResponsive";
import { EmailTemplates } from "./features/templates/components/EmailTemplates";
import { LegalMentionsManagement } from "./features/admin/components/LegalMentionsManagement";
import { InvoicesListResponsive } from "./features/billing/components/InvoicesListResponsive";
import LandingPage from "./features/shared/components/LandingPage";
import { Login, OAuthCallback, MagicLinkVerify, UserAccountSettings } from "./features/auth";
import NotificationContainer from "./features/shared/components/NotificationContainer";
import { OnboardingRoute } from "./features/onboarding/components/OnboardingRoute";
import { PaymentReminders } from "./features/payments/components/PaymentReminders";
import { PaymentsMigrationPage } from "./features/payments/components/PaymentsMigrationPage";
import { GoCardlessMandateComplete } from "./features/payments/components/GoCardlessMandateComplete";
import PricingPage from "./features/subscriptions/components/PricingPage";
import ProductsManagementResponsive from "./features/catalog/components/ProductsManagementResponsive";
import { SuppliersManagement } from "./features/catalog/components/SuppliersManagement";
import { PublicInvoiceView } from "./features/billing/components/PublicInvoiceView";
import { PublicQuoteView } from "./features/billing/components/PublicQuoteView";
import { PublicPaymentPage } from "./features/public-payment";
import { QuotesListResponsive } from "./features/billing/components/QuotesListResponsive";
import CreditNotesManagement from "./features/billing/components/CreditNotes";
import { PublicLinksDashboard } from "./features/billing/components/PublicLinksDashboard";
import {
  InvoiceEditRoute,
  InvoiceNewRoute,
  InvoiceViewRoute,
} from "./features/billing/routes/InvoiceRoutes";
import {
  QuoteEditRoute,
  QuoteNewRoute,
  QuoteViewRoute,
} from "./features/billing/routes/QuoteRoutes";
import {
  CreditNoteEditRoute,
  CreditNoteNewRoute,
  CreditNoteViewRoute,
} from "./features/billing/routes/CreditNoteRoutes";
import {
  RecurringInvoicesListRoute,
  RecurringInvoiceViewRoute,
  RecurringInvoiceNewRoute,
  RecurringInvoiceEditRoute,
} from "./features/billing/routes/RecurringInvoiceRoutes";
import ProtectedSuperAdminRoute from "./features/superadmin/components/ProtectedSuperAdminRoute";
import { TemplateSelector } from "./features/templates/components/TemplateSelector";
import { UsersManagement } from "./features/admin/components/UsersManagement";
import { CompaniesManagement } from "./features/admin/components/CompaniesManagement";
import { TenantSettings, TenantSettingsIndex } from "./features/admin/components/TenantSettings";
import { TenantBilling } from "./features/admin/components/TenantBilling";
import { SecuritySettings } from "./features/admin/components/SecuritySettings";
import { SystemOverview } from "./features/admin/components/SystemOverview";
import { UserProvisioning } from "./features/admin/components/UserProvisioning";
import { CertificateManagement } from "./features/admin/components/CertificateManagement";
import { BreachCheck } from "./features/admin/components/BreachCheck";
import { PermissionsMatrix } from "./features/admin";
import { SessionProvider } from "./contexts/SessionContext";
import { getAccessToken, setAccessToken } from "./services/tokenManager";
import { useAuthStore } from "./stores/authStore";
import { authAPI, sessionsAPI } from "./utils/api";
import { logger } from "./utils/logger";
import ErrorBoundary from "./features/shared/components/ErrorBoundary";
import { ImpersonationBanner } from "./features/shared/components/common/ImpersonationBanner";
import { useOnboarding } from "./hooks/useOnboarding";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NotificationProvider } from "./features/notifications";
import { ChatWidget } from "./features/chat";
import { AcceptInvitationPage } from "./features/cabinet";
import {
  SettingsLayout,
  CompanySettingsPage,
  LegalSettingsPage,
  PaymentsSettingsPage,
  IntegrationsSettingsPage,
  AppearanceSettingsPage,
  SecuritySettingsPage,
  SubscriptionSettingsPage,
} from "./features/settings";
import { ImportPage, ImportHistoryPage } from "./features/import";

// ==================== SUSPENSE FALLBACK ====================
const PageLoader = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-gray-500 text-sm">{t('common.loading')}</p>
      </div>
    </div>
  );
};

// Portal Wrappers - Extract token from URL
function ClientPortalViewWrapper() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const projectId = window.location.pathname.split('/').pop() || '';
  return <ClientPortalView projectId={projectId} token={token} />;
}

function SubcontractorPortalViewWrapper() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  return <SubcontractorPortalView token={token} />;
}

function CollaboratorManagementWrapper() {
  const { projectId = '' } = useParams<{ projectId: string }>();
  // "Projet" is a display-only fallback; CollaboratorManagement
  // uses projectName in the header while project data is not fetched here
  // to avoid adding heavy hook dependencies to this wrapper.
  return <CollaboratorManagement projectId={projectId} projectName="Projet" />;
}

// ✅ Configuration TanStack Query (React Query)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 2 * 60 * 1000, // 2 minutes (réduit de 5 min)
      gcTime: 5 * 60 * 1000, // Garbage collect après 5 minutes
      refetchOnWindowFocus: true, // Refetch quand l'utilisateur revient
      refetchOnReconnect: true, // Refetch après reconnexion réseau
    },
  },
});

function AppContent() {
  const { t } = useTranslation();
  // ✅ Utilisation de Zustand au lieu de useState
  const {
    user,
    isAuthenticated,
    needsCompany,
    logout,
    setUser,
    setNeedsCompany,
  } = useAuthStore();

  // ✅ TanStack Query pour vérification/refresh de session
  // TanStack Query gère automatiquement le dédup (pas de double appel en StrictMode)
  const { isLoading: isInitializing, isError } = useQuery({
    queryKey: ['auth', 'verify-app'],
    queryFn: async () => {
      let token = getAccessToken();

      // Si pas de token en mémoire, tenter un refresh via cookie HttpOnly
      if (!token) {
        logger.log("🔄 AppContent: Pas de token, tentative de refresh via cookie...");
        try {
          const refreshResult = await sessionsAPI.refresh();
          if (refreshResult?.accessToken) {
            const expiresIn = refreshResult.expiresIn || 900;
            setAccessToken(refreshResult.accessToken, expiresIn);
            token = refreshResult.accessToken;
            logger.log("✅ AppContent: Token restauré via cookie HttpOnly");
          }
        } catch (refreshError) {
          logger.log("ℹ️ AppContent: Pas de session valide (refresh échoué)");
          throw new Error('No valid session');
        }
      }

      // Si on a un token mais pas de user dans le store, charger le user
      if (token && !user) {
        logger.log("🔄 AppContent: Rechargement du user depuis le backend...");
        try {
          const userData = await authAPI.me();

          if (userData) {
            logger.log("✅ AppContent: User chargé:", userData.email);
            setUser(userData);

            const hasCompany = !!(
              userData.companyId &&
              userData.companyId !== null &&
              userData.companyId !== undefined
            );

            if (!hasCompany) {
              logger.log("❌ AppContent: Pas de société détectée");
              setNeedsCompany(true);
            } else {
              logger.log("✅ AppContent: Société détectée, ID:", userData.companyId);
              setNeedsCompany(false);
            }
            return userData;
          }
          throw new Error('Failed to load user');
        } catch (error) {
          logger.error("Erreur lors de la vérification de l'authentification:", error);
          if (error instanceof Error && error.message.includes("429")) {
            logger.warn("Rate limit atteint, garde la session locale");
            return user;
          }
          logout();
          throw error;
        }
      }

      return user;
    },
    retry: false,
    staleTime: 30 * 1000, // 30 secondes
    enabled: !isAuthenticated || !user, // Ne lance que si pas déjà authentifié
  });

  // ✅ Hook onboarding avec auto-détection - APRÈS que l'auth soit prête
  // Le hook ne fetch que quand enabled=true pour éviter les 401 pendant le refresh
  const { status, isLoading: onboardingLoading } = useOnboarding({
    enabled: !isInitializing && isAuthenticated,
  });

  // ✅ SUPERADMIN a accès à tout (routes admin tenant + superadmin panel)
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // ✅ Afficher un loader pendant la vérification d'auth
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
  };

  // Si pas authentifié, rediriger vers la landing page
  if (!isAuthenticated) {
    return <Navigate to="/landing" replace />;
  }

  // ✅ Attendre que l'onboarding soit chargé (sauf pour SUPERADMIN)
  // C'est CRITIQUE pour éviter de bypasser l'onboarding après F5
  if (!isSuperAdmin && onboardingLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('common.verifyingConfig')}</p>
        </div>
      </div>
    );
  }

  // ✅ Rediriger vers l'onboarding uniquement basé sur le statut backend
  // SAUF pour les SUPERADMIN qui n'ont pas besoin d'onboarding
  // NOTE: On ne dépend PAS de needsCompany (état Zustand stale après création company)
  // Le backend gère l'auto-détection (company existe → steps auto-détectés)
  const shouldGoToOnboarding = !isSuperAdmin &&
    status !== undefined &&
    status.progress !== null &&
    !status.progress.isCompleted;

  if (shouldGoToOnboarding) {
    logger.log('🔄 AppContent: Redirection vers onboarding', {
      isCompleted: status?.progress?.isCompleted,
      stepsCompleted: status?.stepsCompleted,
    });
    return <Navigate to="/onboarding" replace />;
  }
  const isAdmin = isSuperAdmin || user?.isCompanyAdmin || user?.role === "ADMIN" || user?.role === "OWNER";

  return (
    <NotificationProvider debug={process.env.NODE_ENV === 'development'}>
      {/* Chat Widget - Phase 6.5 CFO Virtuel */}
      <ChatWidget />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Redirection de la racine vers dashboard pour utilisateurs authentifiés */}
        <Route index element={<Navigate to="/dashboard" replace />} />

        {/* Route par défaut - Dashboard */}
        <Route
          path="/"
          element={
            <DashboardMainResponsive onLogout={handleLogout} user={user} />
          }
        >
          {/* Routes imbriquées pour chaque section */}
          <Route path="dashboard" element={<DashboardOverview />} />

          {/* Routes pour les devis avec sous-routes */}
          <Route path="quotes">
            <Route index element={<QuotesListResponsive />} />
            <Route path="new" element={<QuoteNewRoute />} />
            <Route path=":id" element={<QuoteViewRoute />} />
            <Route path=":id/edit" element={<QuoteEditRoute />} />
          </Route>

          {/* Routes pour les factures avec sous-routes */}
          <Route path="invoices">
            <Route index element={<InvoicesListResponsive />} />
            <Route path="new" element={<InvoiceNewRoute />} />
            <Route path=":id" element={<InvoiceViewRoute />} />
            <Route path=":id/edit" element={<InvoiceEditRoute />} />
          </Route>

          {/* Routes pour les avoirs (credit notes) avec sous-routes */}
          <Route path="credit-notes">
            <Route index element={<CreditNotesManagement />} />
            <Route path="new" element={<CreditNoteNewRoute />} />
            <Route path=":id" element={<CreditNoteViewRoute />} />
            <Route path=":id/edit" element={<CreditNoteEditRoute />} />
          </Route>

          {/* Routes pour les factures récurrentes (TASK-301) */}
          <Route path="recurring-invoices">
            <Route index element={<RecurringInvoicesListRoute />} />
            <Route path="new" element={<RecurringInvoiceNewRoute />} />
            <Route path=":id" element={<RecurringInvoiceViewRoute />} />
            <Route path=":id/edit" element={<RecurringInvoiceEditRoute />} />
          </Route>

          <Route path="clients" element={<ClientsManagementResponsive />} />
          <Route path="suppliers" element={<SuppliersManagement />} />
          <Route path="products" element={<ProductsManagementResponsive />} />
          <Route path="reminders" element={<PaymentReminders />} />
          <Route path="payments/migration" element={<PaymentsMigrationPage />} />
          <Route path="payments/mandates/gocardless/complete" element={<GoCardlessMandateComplete />} />
          <Route path="accounting" element={<AccountingDashboard />} />
          <Route path="chart-of-accounts" element={<ChartOfAccountsManagement />} />
          {/* New tabbed pages (Sprint Compta UX) */}
          <Route path="accounting-entries" element={<AccountingEntryAndReconciliation />} />
          <Route path="accounting-balance" element={<AccountingBalanceAndLedger />} />
          <Route path="accounting-declarations" element={<AccountingDeclarationsHub />} />
          {/* Old routes → redirect to new tabbed pages */}
          <Route path="journal-entries" element={<Navigate to="/accounting-entries?tab=entries" replace />} />
          <Route path="account-reconciliation" element={<Navigate to="/accounting-entries?tab=reconciliation" replace />} />
          <Route path="general-balance" element={<Navigate to="/accounting-balance?tab=balance" replace />} />
          <Route path="general-ledger" element={<Navigate to="/accounting-balance?tab=ledger" replace />} />
          <Route path="accounting-exports" element={<Navigate to="/accounting-declarations?tab=exports" replace />} />
          <Route path="imports" element={<ImportPage />} />
          <Route path="imports/history" element={<ImportHistoryPage />} />
          <Route path="financial-statements" element={<FinancialStatementsView />} />
          <Route path="fiscal-year-closure" element={<FiscalYearClosureView />} />
          {/* Sprint 2.2: Analytique Projet */}
          <Route path="project-analytics" element={<MarginDashboard />} />
          <Route path="tax-rates" element={<TaxRatesManagement />} />
          {/* Tax Feature - redirigé vers la page TVA & Conformité */}
          <Route path="tva" element={<Navigate to="/accounting-declarations?tab=tva" replace />} />
          {/* Redirects anciennes URLs → page unifiée */}
          <Route path="vat-dashboard" element={<Navigate to="/accounting-declarations?tab=tva" replace />} />
          <Route path="vat-periods" element={<Navigate to="/accounting-declarations?tab=tva" replace />} />
          <Route path="vat-declarations" element={<Navigate to="/accounting-declarations?tab=tva" replace />} />
          <Route path="deb-des" element={<Navigate to="/accounting-declarations?tab=tva" replace />} />
          <Route path="fiscal-years" element={<FiscalYearManagement />} />
          <Route path="reminder-policies" element={<ReminderPoliciesManagement />} />
          {/* Treasury routes - replaces old /cash-forecast */}
          <Route
            path="treasury"
            element={
              user?.companyId ? (
                <TreasuryDashboard />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="expenses/recurring"
            element={
              user?.companyId ? (
                <RecurringExpensesManagement companyId={user.companyId} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="treasury/budgets"
            element={
              user?.companyId ? (
                <BudgetsManagement companyId={user.companyId} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          {/* Legacy redirect - old cash-forecast to new treasury */}
          <Route path="cash-forecast" element={<Navigate to="/treasury" replace />} />
          {/* Treasury sub-pages */}
          <Route
            path="treasury/forecasts"
            element={
              user?.companyId ? (
                <ForecastsManagement companyId={user.companyId} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="treasury/alerts"
            element={
              user?.companyId ? (
                <AlertsManagement companyId={user.companyId} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="treasury/recommendations"
            element={
              user?.companyId ? (
                <RecommendationsManagement companyId={user.companyId} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="treasury/health-score"
            element={
              user?.companyId ? (
                <HealthScoreManagement companyId={user.companyId} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          {/* AI Actions Feature - Phase 6.4 */}
          <Route path="ai-actions" element={<AIActionsPage />} />
          {/* Benchmarks Feature - Phase 6.6 Intelligence Collective */}
          <Route path="benchmarks" element={<BenchmarksDashboard />} />
          {/* Financing Feature - Phase 6.7 Smart Invoice Financing */}
          <Route path="financing" element={<FinancingDashboard />} />
          {/* Cabinet Comptable Feature - Phase 3 Multi-Tenant Roles */}
          <Route path="cabinet/clients" element={<CabinetClientsManagement />} />
          <Route path="cabinet/dossier/:id" element={<ClientDossierPage />} />
          <Route path="cabinet/exports" element={<CabinetExportsPage />} />
          <Route path="email-logs" element={<EmailLogsManagement />} />
          <Route path="audit-logs" element={<AuditLogsManagement />} />
          <Route path="webhooks" element={<WebhooksManagement />} />
          {/* Admin Features - ISO 27001 */}
          <Route path="system-overview" element={<SystemOverview />} />
          <Route path="user-provisioning" element={<UserProvisioning />} />
          <Route path="certificates" element={<CertificateManagement />} />
          <Route path="breach-check" element={<BreachCheck />} />
          {/* Achats - Module Unifié V3 (remplace purchases et expenses) */}
          <Route path="achats" element={<AchatsDashboard />} />
          {/* Legacy routes - redirigent vers /achats */}
          <Route path="purchases" element={<Navigate to="/achats" replace />} />
          <Route path="expenses" element={<Navigate to="/achats" replace />} />
          <Route path="interventions" element={<InterventionsDashboard />} />
          <Route path="timesheets" element={<TimesheetsManagement />} />
          {/* ERP Chantier - Projets & Portfolio */}
          <Route path="projects" element={<ProjectsManagement />} />
          <Route path="projects/portfolio" element={<PortfolioDashboardPage />} />
          <Route path="projects/:projectId" element={<ProjectDetailPage />} />
          {/* Gestion des collaborateurs projet - Phase 4 */}
          <Route path="projects/:projectId/collaborators" element={<CollaboratorManagementWrapper />} />
          {/* ERP Chantier - Routes menu principal (Vision Leader) */}
          <Route path="erp-cockpit" element={<CockpitDashboard />} />
          <Route path="erp-terrain" element={<TerrainDashboard />} />
          <Route path="erp-collaboration" element={<CollaborationPage />} />
          <Route path="erp-portfolio" element={<PortfolioDashboardPage />} />
          {/* Legacy route redirect */}
          <Route path="terrain" element={<Navigate to="/erp-terrain" replace />} />
          <Route path="erp-projects" element={<ProjectsManagement />} />
          <Route path="erp-situations" element={<ERPSituationsPage />} />
          <Route path="erp-planning" element={<ERPPlanningPage />} />
          <Route path="erp-teams" element={<ERPTeamsPage />} />
          <Route path="erp-documents" element={<ERPDocumentsPage />} />
          <Route path="erp-materials" element={<ERPMaterialsPage />} />
          <Route path="erp-subcontracting" element={<ERPSubcontractingPage />} />
          {/* Nouvelles vues UX métier (Phase 3) */}
          <Route path="erp-ma-journee" element={<MyDayView />} />
          <Route path="erp-facturer" element={<FacturerView />} />
          {/* Sprint 2.1: Gestion Sous-Traitants BTP */}
          <Route path="subcontracts" element={<SubcontractsPage />} />
          <Route path="work-orders" element={<WorkOrdersManagement />} />
          <Route path="planning" element={<PlanningCalendar />} />
          <Route path="field-mode" element={<FieldModeView />} />
          {/* CRM Vente - Pipeline Commercial (Add-on 1) */}
          <Route path="crm-vente" element={<CRMDashboard />} />
          <Route path="leads" element={<LeadsManagement />} />
          <Route path="opportunities" element={<OpportunitiesPipeline />} />
          <Route path="sales-tasks" element={<SalesTasksManagement />} />
          <Route path="automation-rules" element={<AutomationRulesManagement />} />

          {/* ERP Chantier - Nouvelle Architecture 3 Modes (Vision 2026) */}
          <Route path="erp" element={<ERPLayout />}>
            <Route index element={<Navigate to="/erp/accueil" replace />} />
            <Route path="accueil" element={<AccueilFeed />} />
            {/* Mode Terrain */}
            <Route path="terrain" element={<TerrainLayout />}>
              <Route index element={<TerrainHome />} />
              <Route path="pointage" element={<QuickPointing />} />
              <Route path="avancement" element={<AdvancementCapture />} />
              <Route path="incidents" element={<IncidentReport />} />
              <Route path="heures" element={<MaSemaine />} />
              <Route path="heures/:date" element={<DetailJournee />} />
              <Route path="heures/soumettre" element={<SoumissionSemaine />} />
              <Route path="validation" element={<EquipeTempsReel />} />
              <Route path="validation/feuilles" element={<FeuillesEnAttente />} />
            </Route>
            {/* Mode Argent */}
            <Route path="argent" element={<ArgentLayout />}>
              <Route index element={<ArgentHome />} />
              <Route path="facturer" element={<FacturationAssistant />} />
              <Route path="encaisser" element={<EncaissementsView />} />
              <Route path="alertes" element={<AlerteDepassement />} />
              <Route path="payer" element={<ArgentHome />} />
              <Route path="prevoir" element={<ArgentHome />} />
              <Route path="piloter" element={<ArgentHome />} />
            </Route>
            {/* Mode Dossiers */}
            <Route path="dossiers" element={<DossiersLayout />}>
              <Route index element={<DossiersHome />} />
              <Route path="chantiers" element={<ProjectsManagement />} />
              <Route path="chantiers/:id" element={<ProjectDetailPage />} />
              <Route path="clients" element={<ClientsManagementResponsive />} />
              <Route path="equipes" element={<ERPTeamsPage />} />
              <Route path="fournisseurs" element={<SuppliersManagement />} />
            </Route>
          </Route>

          <Route path="chorus-pro" element={<Navigate to="/accounting-declarations?tab=chorus" replace />} />
          <Route path="probatory-archive" element={<Navigate to="/accounting-declarations?tab=archive" replace />} />
          <Route path="bank-reconciliation" element={<BankReconciliation />} />
          <Route path="three-way-match" element={<ThreeWayMatching />} />

          {/* TASK-101: Support Tickets */}
          <Route path="support">
            <Route index element={<SupportPage />} />
            <Route path="new" element={<TicketCreateForm />} />
            <Route path=":id" element={<TicketDetailWrapper />} />
          </Route>

          {/* TASK-102: Knowledge Base (lecture seule pour users) */}
          <Route path="help" element={<HelpCenterPage />} />
          <Route path="ocr-extraction" element={<OCRInvoiceExtraction />} />
          <Route
            path="accounting-connectors"
            element={<AccountingConnectorsSettings />}
          />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="billing" element={<BillingDashboard />} />
          <Route path="public-links" element={<PublicLinksDashboard />} />

          {/* Paramètres organisés par catégorie */}
          <Route
            path="settings"
            element={
              user?.companyId ? (
                <SettingsLayout />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          >
            <Route index element={<Navigate to="/settings/company" replace />} />
            <Route path="company" element={<CompanySettingsPage />} />
            <Route path="legal" element={<LegalSettingsPage />} />
            <Route path="payments" element={<PaymentsSettingsPage />} />
            <Route path="integrations" element={<IntegrationsSettingsPage />} />
            <Route path="appearance" element={<AppearanceSettingsPage />} />
            <Route path="security" element={<SecuritySettingsPage />} />
            <Route path="subscription" element={<SubscriptionSettingsPage />} />
          </Route>

          {/* Paramètres du compte utilisateur (profil + sécurité) */}
          <Route path="settings/account" element={<UserAccountSettings />} />
          {/* Routes admin uniquement */}
          <Route
            path="users"
            element={
              isAdmin ? (
                <UsersManagement />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="companies"
            element={
              isAdmin ? (
                <CompaniesManagement />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          {/* Paramètres Tenant - ISO 27001 A.9.2.1 */}
          <Route
            path="settings/tenant"
            element={
              isAdmin ? (
                <TenantSettings />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          >
            <Route index element={<TenantSettingsIndex />} />
            <Route path="companies" element={<CompaniesManagement />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="security" element={<SecuritySettings />} />
            <Route path="audit" element={<AuditLogsManagement />} />
            <Route path="billing" element={<TenantBilling />} />
            <Route path="permissions" element={<PermissionsMatrix />} />
          </Route>
          <Route
            path="templates"
            element={
              isAdmin ? (
                <TemplateSelector />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="email-templates"
            element={
              isAdmin ? (
                <EmailTemplates />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="legal-mentions"
            element={
              isAdmin ? (
                <LegalMentionsManagement />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="api-keys"
            element={
              isAdmin ? (
                <ApiKeysManagement />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="signatures"
            element={
              isAdmin ? (
                <SignaturesManagement />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
        </Route>
        {/* Cabinet Comptable - Accept Invitation (full-screen page) */}
        <Route path="cabinet/accept-invitation" element={<AcceptInvitationPage />} />
      </Routes>
      </Suspense>
    </NotificationProvider>
  );
}

/**
 * Wrapper for TicketDetail to use route params
 */
function TicketDetailWrapper() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/support" replace />;
  return <TicketDetail ticketId={id} onBack={() => window.history.back()} />;
}

function App() {
  const { login } = useAuthStore();
  const [showDebugPanel, setShowDebugPanel] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("debug") === "facturx";
  });

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setShowDebugPanel((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // handleLogin est appelé par Login.tsx après une connexion réussie
  // Le token est déjà stocké en mémoire via setAccessToken dans useSession.setSession
  const handleLogin = async (_token: string, userData: any) => {
    login(userData);
  };

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <ImpersonationBanner />
          <SessionProvider>
            <Suspense fallback={<PageLoader />}>
            <Routes>
            {/* Routes publiques */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/login" element={<Login onLogin={handleLogin} />} />

            {/* OAuth Callback - SSO Google/Microsoft */}
            <Route path="/auth/callback" element={<OAuthCallback />} />

            {/* Magic Link Verification - Passwordless Login */}
            <Route path="/auth/magic-link/verify" element={<MagicLinkVerify />} />

            {/* ❌ Deprecated: Utiliser /onboarding à la place */}
            {/* <Route
              path="/company-setup"
              element={
                <CompanyCreation onCompanyCreated={handleCompanyCreated} />
              }
            /> */}

            {/* Routes publiques pour visualiser les devis et factures */}
            <Route path="/q/:token" element={<PublicQuoteView />} />
            <Route path="/i/:token" element={<PublicInvoiceView />} />

            {/* Route publique pour payer une facture */}
            <Route path="/pay/:token" element={<PublicPaymentPage />} />

            {/* Portails publics - Phase 4 Collaboration */}
            <Route path="/portal/client/:projectId" element={<ClientPortalViewWrapper />} />
            <Route path="/portal/subcontractor" element={<SubcontractorPortalViewWrapper />} />

            {/* Portail Client - Maîtres d'ouvrage (Magic Link Auth) */}
            <Route path="/portal/login" element={<ClientPortalLoginPage />} />
            <Route path="/portal" element={<ClientPortalLayout />}>
              <Route index element={<Navigate to="/portal/dashboard" replace />} />
              <Route path="dashboard" element={<ClientPortalDashboard />} />
              <Route path="projects" element={<ClientProjectsList />} />
              <Route path="projects/:id" element={<ClientProjectDetail />} />
              <Route path="messages" element={<ClientMessagesPanel />} />
            </Route>

            {/* Route pour l'onboarding (création société + configuration complète) */}
            {/* ✅ OnboardingRoute gère l'auth AVANT de rendre OnboardingWizard */}
            <Route path="/onboarding" element={<OnboardingRoute />} />

            {/* Routes Super Admin - Protégées */}
            <Route
              path="/superadmin"
              element={
                <ProtectedSuperAdminRoute>
                  <SuperAdminLayout user={undefined} />
                </ProtectedSuperAdminRoute>
              }
            >
              <Route
                index
                element={<Navigate to="/superadmin/dashboard" replace />}
              />
              <Route path="dashboard" element={<SuperAdminDashboard />} />
              <Route path="tenants" element={<SuperAdminTenantsManagement />} />
              <Route path="users" element={<SuperAdminUsersManagement />} />
              <Route path="company-users" element={<SuperAdminCompanyUsersManagement />} />
              <Route
                path="companies"
                element={<SuperAdminCompaniesManagement />}
              />
              <Route
                path="subscriptions"
                element={<SuperAdminSubscriptionsManagement />}
              />
              <Route
                path="plans"
                element={<SuperAdminPlansManagement />}
              />
              <Route
                path="templates"
                element={<SuperAdminTemplatesManagement />}
              />
              <Route
                path="payments"
                element={<SuperAdminPaymentsManagement />}
              />
              <Route
                path="tickets"
                element={<SuperAdminTicketsManagement />}
              />
              <Route
                path="security"
                element={<SuperAdminSecurityManagement />}
              />
              <Route path="emails" element={<SuperAdminEmailManagement />} />
              <Route path="analytics" element={<SuperAdminAnalytics />} />
              <Route path="monitoring" element={<MonitoringDashboard />} />
              <Route
                path="session-metrics"
                element={<SuperAdminSessionMetrics />}
              />
              <Route path="logs" element={<SuperAdminSystemLogs />} />
              <Route path="audit-logs" element={<SuperAdminAuditLogs />} />
              <Route path="config" element={<SuperAdminPlatformConfig />} />
              <Route path="feature-flags" element={<FeatureFlagsManagement />} />
              <Route path="broadcast" element={<BroadcastManagement />} />
              <Route path="quotas" element={<QuotasManagement />} />
              <Route path="open-banking" element={<OpenBankingSettings />} />
              <Route path="ocr-settings" element={<SuperAdminOCRSettings />} />
              <Route path="landing-cms" element={<LandingCmsEditor />} />
              {/* TASK-104: System Alerts */}
              <Route path="system-alerts" element={<SystemAlertsPage />} />
            </Route>

            {/* Toutes les autres routes (tableau de bord) */}
            <Route path="/*" element={<AppContent />} />
            </Routes>
            </Suspense>
            {/* Panneau de debug Factur-X */}
            {showDebugPanel && <FacturXDebugPanel />}
          </SessionProvider>
          {/* Conteneur de notifications global */}
          <NotificationContainer />
          {/* Toast notifications (react-hot-toast) */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#374151',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              },
              success: {
                iconTheme: { primary: '#10B981', secondary: '#fff' },
              },
              error: {
                iconTheme: { primary: '#EF4444', secondary: '#fff' },
              },
            }}
          />
          {/* Modal de confirmation global */}
          <ConfirmDialog />
          {/* Modal de saisie texte global (remplace window.prompt) */}
          <PromptDialog />
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
